import React, { useMemo } from 'react';
import {
  Box, Typography, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Card, CardActionArea, CardContent, Divider, alpha, useTheme, Alert,
} from '@mui/material';
import {
  SKLEARN_ESTIMATORS, XGBOOST_HYPERPARAMETERS, defaultHyperparameters, coerceHyperparam,
} from './utils/frameworkSchemas';

/**
 * EstimatorBuilder
 *
 * Replaces the layer editor when the framework is sklearn or
 * XGBoost. sklearn shows a card grid of estimators + a
 * hyperparameter form for the chosen one; XGBoost goes straight to
 * a hyperparameter form (only one estimator).
 *
 * Uses ONLY theme tokens for colors / spacing - no hardcoded
 * brand colors. Adapts to light + dark themes automatically.
 */
const EstimatorBuilder = ({ framework, modelBlueprint, onChange }) => {
  const theme = useTheme();

  // Hooks must run unconditionally on every render (rules-of-hooks).
  const selectedName = modelBlueprint.estimator || 'RandomForestRegressor';
  const selected = useMemo(
    () => SKLEARN_ESTIMATORS.find((e) => e.name === selectedName) || SKLEARN_ESTIMATORS[0],
    [selectedName]
  );
  const hp = modelBlueprint.hyperparameters || {};

  if (framework === 'xgboost') {
    return (
      <Box>
        <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
          XGBoost hyperparameters
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Tree-based gradient boosting. Multi-output forecasts are wrapped automatically.
        </Typography>
        <HyperparameterGrid
          params={XGBOOST_HYPERPARAMETERS}
          values={hp}
          onChange={(next) => onChange({ ...modelBlueprint, hyperparameters: next })}
        />
      </Box>
    );
  }

  const pickEstimator = (name) => {
    const def = SKLEARN_ESTIMATORS.find((e) => e.name === name);
    if (!def) return;
    onChange({
      ...modelBlueprint,
      estimator: name,
      hyperparameters: defaultHyperparameters(def.hyperparameters),
    });
  };

  return (
    <Box>
      <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
        Pick an estimator
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        sklearn doesn&apos;t use a layer editor &mdash; pick an estimator and tune its hyperparameters.
        Multi-step forecasts are wrapped automatically.
      </Typography>

      <Grid container spacing={2}>
        {SKLEARN_ESTIMATORS.map((e) => {
          const isSelected = e.name === selectedName;
          return (
            <Grid item xs={12} sm={6} md={4} key={e.name}>
              <Card
                variant="outlined"
                sx={{
                  borderWidth: isSelected ? 2 : 1,
                  borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider,
                  bgcolor: isSelected
                    ? alpha(theme.palette.primary.main, 0.04)
                    : theme.palette.background.paper,
                  height: '100%',
                  transition: theme.transitions.create(['border-color', 'background-color']),
                  '&:hover': { borderColor: theme.palette.primary.main },
                }}
              >
                <CardActionArea onClick={() => pickEstimator(e.name)} sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {e.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {e.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
          {selected.name} hyperparameters
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {selected.hyperparameters.length === 0 ? (
          <Alert severity="info" variant="outlined">
            {selected.name} has no tunable hyperparameters in this wizard.
          </Alert>
        ) : (
          <HyperparameterGrid
            params={selected.hyperparameters}
            values={hp}
            onChange={(next) => onChange({ ...modelBlueprint, hyperparameters: next })}
          />
        )}
      </Box>
    </Box>
  );
};

const HyperparameterGrid = ({ params, values, onChange }) => (
  <Grid container spacing={2}>
    {params.map((p) => {
      const value = values[p.key] !== undefined ? values[p.key] : p.default;
      const set = (raw) => onChange({ ...values, [p.key]: coerceHyperparam(p, raw) });
      if (p.type === 'select') {
        return (
          <Grid item xs={12} md={6} lg={4} key={p.key}>
            <FormControl fullWidth>
              <InputLabel>{p.label}</InputLabel>
              <Select label={p.label} value={value ?? p.default ?? ''} onChange={(e) => set(e.target.value)}>
                {p.options.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        );
      }
      return (
        <Grid item xs={12} md={6} lg={4} key={p.key}>
          <TextField
            fullWidth
            type="number"
            label={p.label}
            value={value ?? ''}
            onChange={(e) => set(e.target.value)}
            inputProps={{
              min: p.min,
              max: p.max,
              step: p.step || (p.type === 'int' ? 1 : 0.01),
            }}
            helperText={p.optional ? 'Optional' : ' '}
          />
        </Grid>
      );
    })}
  </Grid>
);

export default EstimatorBuilder;
